import React, { useState } from 'react';
import { Card, CardBody, Button, Input, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { formatCurrency } from '../../../../utils/format';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { getMethodIcon } from '../constants';

const SalesCartPanel = ({ 
    cartItems, 
    payments, 
    totals, 
    onRemoveItem, 
    onRemovePayment, 
    onProceed,
    // Props de Matrícula Pós-Venda
    pendingEnrollments = {}, 
    onOpenEnrollment = null, 
    onClearEnrollment = null
}) => {
    const [dueDate, setDueDate] = useState('');
    const [showModal, setShowModal] = useState(false);

    // Cálculos de Totais (Usa totals do hook se disponível, senão recalcula)
    const subtotal = totals?.subtotal ?? cartItems.reduce((acc, curr) => acc + (parseFloat(curr.price) || 0), 0);
    const totalPaid = totals?.totalPaid ?? payments.reduce((acc, curr) => acc + (parseFloat(curr.value) || 0), 0);
    const balance = totals?.balance ?? (subtotal - totalPaid);

    const handleInitialFinalize = () => {
        if (balance > 0 && !dueDate) {
            setShowModal(true);
        } else {
            handleConfirmFinalize();
        }
    };

    const handleConfirmDueDate = () => {
        setShowModal(false);
        // Apenas salva a data e fecha o modal. A venda NÃO é finalizada aqui.
    };

    const handleConfirmFinalize = () => {
        setShowModal(false);
        onProceed({
            balancePending: balance > 0,
            dueDate: balance > 0 ? dueDate : null,
            totalItems: subtotal,
            totalPaid: totalPaid
        });
    };

    return (
        <React.Fragment>
            <Card className="neo-stat-card animate__animated animate__fadeIn" style={{ marginTop: '2px' }}>
                <CardBody className="d-flex flex-column">
                    <h5 className="font-size-15 fw-bold mb-4 text-uppercase" style={{ color: 'var(--color-brand-primary)', letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Resumo da Venda</h5>

                    <div className="flex-grow-1">
                        {/* LISTA DE ITENS */}
                        <div className="mb-4">
                            <label className="text-muted font-size-11 fw-bold text-uppercase mb-2 d-block" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}>Itens da venda</label>
                            {cartItems.length === 0 ? (
                                <div className="text-center py-3 border rounded-3 opacity-50 mb-3" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                                    <span className="text-muted font-size-12 fw-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Nenhum item selecionado</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2 mb-3">
                                    {cartItems.map((item) => (
                                        <div key={item.cartId} className="p-3 border rounded-3 bg-white shadow-sm animate__animated animate__fadeIn mb-2" style={{ borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                                            <div className="d-flex justify-content-between align-items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                <div>
                                                    <span className="fw-bold font-size-13 d-block pe-2" style={{ color: 'var(--color-brand-primary)' }}>{item.name}</span>
                                                </div>
                                                <div className="text-end d-flex align-items-center">
                                                    <span className="fw-bold me-2" style={{ color: 'var(--color-brand-primary)' }}>{formatCurrency(item.price || item.unitPrice)}</span>
                                                    <Button color="link" size="sm" className="p-0 text-danger" onClick={() => onRemoveItem(item.cartId)}>
                                                        <i className="mdi mdi-close-circle-outline font-size-18"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* LISTA DE PAGAMENTOS */}
                        <div className="mb-4">
                            <label className="text-muted font-size-11 fw-bold text-uppercase mb-2 d-block" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}>Pagamentos Realizados</label>
                            {payments.length === 0 ? (
                                <div className="text-center py-3 border rounded-3 opacity-50" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                                    <span className="text-muted font-size-12 fw-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Nenhum pagamento registrado</span>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {payments.map((p) => (
                                        <div key={p.id} className="p-3 border rounded-3 d-flex justify-content-between align-items-center animate__animated animate__fadeIn" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                                            <div className="d-flex align-items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                <i className={`mdi ${getMethodIcon(p.methodId)} me-2 font-size-20`} style={{ color: 'var(--color-brand-accent)' }}></i>
                                                <div>
                                                    <span className="fw-bold font-size-13 d-block" style={{ color: 'var(--color-brand-primary)' }}>{p.methodLabel}</span>
                                                    <small className="text-muted fw-medium">
                                                        {[PAYMENT_METHODS.CREDIT_CARD, PAYMENT_METHODS.DEBIT_CARD].includes(p.methodId)
                                                            ? `${p.brand.toUpperCase()} - ${p.installments}x`
                                                            : 'À vista'}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="text-end d-flex align-items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                <span className="fw-bold text-success me-2">{formatCurrency(p.value)}</span>
                                                <Button color="link" size="sm" className="p-0 text-danger" onClick={() => onRemovePayment(p.id)}>
                                                    <i className="mdi mdi-trash-can-outline font-size-18"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RESUMO FINANCEIRO */}
                    <div className="cart-summary mt-auto pt-3 border-top" style={{ borderColor: 'rgba(72, 39, 18, 0.12)', fontFamily: 'Outfit, sans-serif' }}>
                        <div className="d-flex justify-content-between mb-2 text-muted font-size-13 fw-semibold">
                            <span>Subtotal</span>
                            <span style={{ color: 'var(--color-brand-primary)' }}>{formatCurrency(subtotal)}</span>
                        </div>

                        {totals?.discount > 0 && (
                            <div className="d-flex justify-content-between mb-2 text-danger font-size-13 fw-bold">
                                <span>Desconto</span>
                                <span>- {formatCurrency(totals.discount)}</span>
                            </div>
                        )}

                        <div className="d-flex justify-content-between mb-2 text-muted font-size-13 fw-semibold">
                            <span>Total Pago</span>
                            <span className="text-success fw-bold">{formatCurrency(totalPaid)}</span>
                        </div>

                        <div className={`d-flex justify-content-between mt-3 pt-3 border-top ${balance > 0 ? 'text-danger' : totals?.surplus > 0 ? 'text-warning' : 'text-success'}`} style={{ borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                            <span className="fw-extrabold font-size-15">
                                {balance > 0 ? 'Saldo Restante' : totals?.surplus > 0 ? 'Excesso (Troco)' : 'Status'}
                            </span>
                            <span className="fw-extrabold font-size-18">
                                {balance > 0
                                    ? formatCurrency(balance)
                                    : totals?.surplus > 0
                                        ? formatCurrency(totals.surplus)
                                        : 'QUITADO'
                                }
                            </span>
                        </div>
                        
                        {/* Exibe botão/área de previsão caso haja saldo devedor */}
                        {balance > 0 && dueDate && (
                            <div className="mt-2 text-end">
                                <div className="d-flex align-items-center justify-content-between p-3 mt-2 rounded-3 border animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(226, 102, 28, 0.05)', borderColor: 'var(--color-brand-accent)' }}>
                                    <div>
                                        <span className="d-block fw-bold font-size-11 text-start" style={{ color: 'var(--color-brand-accent)' }}>PREVISÃO DE RECEBIMENTO</span>
                                        <span className="d-block font-size-13 fw-semibold mt-1" style={{ color: 'var(--color-brand-primary)' }}>
                                            {dueDate.split('-').reverse().join('/')}
                                        </span>
                                    </div>
                                    <Button color="link" className="p-0" onClick={() => setShowModal(true)} style={{ color: 'var(--color-brand-accent)' }}>
                                        <i className="mdi mdi-pencil" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        <Button
                            color="success"
                            size="lg"
                            block
                            className="w-100 fw-bold mt-4 py-3 shadow-sm text-uppercase"
                            onClick={handleInitialFinalize}
                            disabled={cartItems.length === 0 || (subtotal === 0 && payments.length === 0) || cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0))}
                            style={{ borderRadius: '10px', fontSize: '14px', letterSpacing: '0.5px' }}
                        >
                            <i className={`mdi ${cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0)) ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline'} me-2 font-size-17`}></i>
                            {cartItems.some(i => i.enrollmentRequired && (!pendingEnrollments[i.cartId] || pendingEnrollments[i.cartId].length === 0)) ? 'PENDENTE: TURMAS' : 'FINALIZAR VENDA'}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* MODAL DE CONFIRMAÇÃO DE SALDO DEVEDOR */}
            <Modal 
                isOpen={showModal} 
                toggle={() => setShowModal(!showModal)} 
                centered 
                backdrop="static" 
                style={{ fontFamily: 'Outfit, sans-serif' }}
                contentClassName="premium-modal border-0"
            >
                <ModalHeader toggle={() => setShowModal(false)} className="border-bottom py-3 px-4">
                    <div className="d-flex align-items-center">
                        <div 
                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3"
                            style={{
                                width: "36px",
                                height: "36px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)"
                            }}
                        >
                            <i className="mdi mdi-calendar-clock font-size-18" style={{ color: "#e2661c" }}></i>
                        </div>
                        <div>
                            <span className="fw-bold text-dark font-size-15 d-block">Agendar Recebimento</span>
                            <small className="text-muted font-size-11">Definir data prevista para saldo restante</small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4 bg-white">
                    <p className="font-size-14 text-muted mb-4 text-center">
                        A venda ficará com um <strong style={{ color: 'var(--color-brand-primary)' }}>saldo restante de {formatCurrency(balance)}</strong>. 
                        Qual a data prevista para o cliente realizar o pagamento?
                    </p>
                    <div className="border p-3 rounded-3" style={{ borderColor: 'rgba(72, 39, 18, 0.12)', backgroundColor: 'var(--color-bg-canvas)' }}>
                        <label className="text-muted font-size-12 fw-bold text-uppercase mb-2 d-block" style={{ letterSpacing: '0.5px' }}>
                            Data prevista
                        </label>
                        <InputGroup size="sm">
                            <InputGroupText className="bg-white border-end-0" style={{ borderColor: 'rgba(72, 39, 18, 0.15)' }}>
                                <i className="mdi mdi-calendar font-size-15" style={{ color: 'var(--color-brand-accent)' }}></i>
                            </InputGroupText>
                            <Input
                                type="date"
                                className="border-start-0 ps-0 bg-white"
                                value={dueDate || ''}
                                onChange={(e) => setDueDate(e.target.value)}
                                style={{ 
                                    borderColor: 'rgba(72, 39, 18, 0.15)',
                                    borderRadius: '0 10px 10px 0',
                                    fontSize: '13px',
                                    fontFamily: 'Outfit, sans-serif'
                                }}
                            />
                        </InputGroup>
                    </div>
                </ModalBody>
                <ModalFooter className="border-top py-3 px-4 d-flex justify-content-end gap-2 bg-light bg-opacity-30">
                    <button 
                        className="btn btn-outline-light border text-dark font-size-13 fw-semibold" 
                        style={{ 
                            borderRadius: "10px", 
                            height: "42px", 
                            minWidth: "120px", 
                            fontFamily: "Outfit, sans-serif", 
                            borderColor: "rgba(72, 39, 18, 0.15)",
                            backgroundColor: "#fff"
                        }}
                        onClick={() => setShowModal(false)}
                    >
                        Cancelar
                    </button>
                    <Button 
                        color="primary" 
                        className="px-4 fw-bold shadow-sm d-flex align-items-center justify-content-center text-white"
                        style={{ 
                            borderRadius: "10px", 
                            height: "42px", 
                            minWidth: "120px", 
                            fontFamily: "Outfit, sans-serif" 
                        }}
                        onClick={handleConfirmDueDate} 
                        disabled={!dueDate}
                    >
                        Confirmar Data
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default SalesCartPanel;
